import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stack,
  Divider,
  Breadcrumbs,
  Link as MuiLink,
  useMediaQuery,
  useTheme,
  Alert,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  Chip
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  ShoppingCart,
  Person,
  LocationOn,
  Payment,
  Email,
  Phone,
  Home,
  Business,
  CreditCard,
  AccountBalance,
  LocalShipping,
  Receipt
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { formatMoney } from '../utils/currency';

// Shipping options
const SHIPPING_OPTIONS = [
  { id: 'standard', name: 'Standard Shipping', price: 5.99, days: '5-7 business days' },
  { id: 'express', name: 'Express Shipping', price: 12.99, days: '2-3 business days' },
  { id: 'overnight', name: 'Overnight Shipping', price: 24.99, days: '1 business day' }
];

// Payment methods
const PAYMENT_METHODS = [
  { id: 'credit', name: 'Credit Card', icon: <CreditCard /> },
  { id: 'bank', name: 'Bank Transfer', icon: <AccountBalance /> }
];

const STEPS = ['Order Details', 'Customer Information', 'Shipping & Payment', 'Review & Submit'];

function OrderForm() {
  const location = useLocation();
  const navigate = useNavigate();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [orderData, setOrderData] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Customer Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    
    // Shipping Address
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    
    // Shipping & Payment
    shippingMethod: 'standard',
    paymentMethod: 'credit',
    
    // Additional Options
    rushOrder: false,
    giftWrapping: false,
    specialInstructions: '',
    
    // Terms
    agreeToTerms: false,
    marketingEmails: false
  });
  
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  useEffect(() => {
    if (location.state) {
      setOrderData(location.state);
    } else {
      // Redirect if no order data
      navigate('/');
    }
  }, [location.state, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 0: // Order Details - no validation needed
        break;
      case 1: // Customer Information
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
        if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
        if (!formData.address.trim()) newErrors.address = 'Address is required';
        if (!formData.city.trim()) newErrors.city = 'City is required';
        if (!formData.state.trim()) newErrors.state = 'State is required';
        if (!formData.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';
        break;
      case 2: // Shipping & Payment
        if (!formData.shippingMethod) newErrors.shippingMethod = 'Please select shipping method';
        if (!formData.paymentMethod) newErrors.paymentMethod = 'Please select payment method';
        break;
      case 3: // Review & Submit
        if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;
    
    try {
      // In a real app, this would send the order to your backend
      const orderPayload = {
        ...orderData,
        customer: formData,
        orderDate: new Date().toISOString(),
        orderNumber: `ORD-${Date.now()}`,
        total: calculateTotal()
      };
      
      // Show success message
      setSnackbarSeverity('success');
      setSnackbarMessage('Order submitted successfully!');
      setSnackbarOpen(true);
      
      // Navigate to thank you page
      setTimeout(() => {
        navigate('/thank-you', { state: { orderNumber: orderPayload.orderNumber } });
      }, 2000);
      
    } catch (error) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Error submitting order. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const calculateSubtotal = () => {
    if (!orderData) return 0;
    return (orderData.design?.price || 29.99) * (orderData.quantity || 1);
  };

  const calculateShipping = () => {
    const shipping = SHIPPING_OPTIONS.find(s => s.id === formData.shippingMethod);
    return shipping ? shipping.price : 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateShipping();
  };

  const renderOrderSummary = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" component="h3" gutterBottom>
          <ShoppingCart sx={{ mr: 1, verticalAlign: 'middle' }} />
          Order Summary
        </Typography>
        
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Design:</Typography>
            <Typography variant="body2" fontWeight="bold">
              {orderData?.design?.name || 'Custom Design'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Category:</Typography>
            <Typography variant="body2" fontWeight="bold">
              {orderData?.category ? orderData.category.charAt(0).toUpperCase() + orderData.category.slice(1) : 'Custom'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Size:</Typography>
            <Typography variant="body2" fontWeight="bold">{orderData?.size}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Quantity:</Typography>
            <Typography variant="body2" fontWeight="bold">{orderData?.quantity}</Typography>
          </Box>
          
          <Divider />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1" fontWeight="bold">Subtotal:</Typography>
            <Typography variant="body1" fontWeight="bold" color="primary">
              {formatMoney(calculateSubtotal())}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Shipping:</Typography>
            <Typography variant="body2">
              {formatMoney(calculateShipping())}
            </Typography>
          </Box>
          
          <Divider />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="bold">Total:</Typography>
            <Typography variant="h6" fontWeight="bold" color="primary">
              {formatMoney(calculateTotal())}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" component="h3" gutterBottom>
                Order Details
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Please review your order details before proceeding.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold">Design Information</Typography>
                  <Typography variant="body2">Name: {orderData?.design?.name}</Typography>
                  <Typography variant="body2">Category: {orderData?.category}</Typography>
                  <Typography variant="body2">Size: {orderData?.size}</Typography>
                  <Typography variant="body2">Quantity: {orderData?.quantity}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold">Customization</Typography>
                  <Typography variant="body2">Logo: {orderData?.logo?.type}</Typography>
                  <Typography variant="body2">Number: {orderData?.number?.value}</Typography>
                  <Typography variant="body2">Team: {orderData?.team?.name}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
        
      case 1:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" component="h3" gutterBottom>
                <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                Customer Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name *"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name *"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email *"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone *"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company (Optional)"
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Shipping Address
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street Address *"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    error={!!errors.address}
                    helperText={errors.address}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City *"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    error={!!errors.city}
                    helperText={errors.city}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="State/Province *"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    error={!!errors.state}
                    helperText={errors.state}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ZIP/Postal Code *"
                    value={formData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    error={!!errors.zipCode}
                    helperText={errors.zipCode}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={formData.country}
                      label="Country"
                      onChange={(e) => handleInputChange('country', e.target.value)}
                    >
                      <MenuItem value="US">United States</MenuItem>
                      <MenuItem value="CA">Canada</MenuItem>
                      <MenuItem value="UK">United Kingdom</MenuItem>
                      <MenuItem value="AU">Australia</MenuItem>
                      <MenuItem value="DE">Germany</MenuItem>
                      <MenuItem value="FR">France</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
        
      case 2:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" component="h3" gutterBottom>
                <LocalShipping sx={{ mr: 1, verticalAlign: 'middle' }} />
                Shipping & Payment
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Shipping Method
                </Typography>
                <RadioGroup
                  value={formData.shippingMethod}
                  onChange={(e) => handleInputChange('shippingMethod', e.target.value)}
                >
                  {SHIPPING_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.id}
                      value={option.id}
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <Box>
                            <Typography variant="body1">{option.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.days}
                            </Typography>
                          </Box>
                          <Typography variant="body1" fontWeight="bold">
                            {formatMoney(option.price)}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
                {errors.shippingMethod && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {errors.shippingMethod}
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Payment Method
                </Typography>
                <RadioGroup
                  value={formData.paymentMethod}
                  onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                >
                  {PAYMENT_METHODS.map((method) => (
                    <FormControlLabel
                      key={method.id}
                      value={method.id}
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {method.icon}
                          <Typography variant="body1">{method.name}</Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
                {errors.paymentMethod && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {errors.paymentMethod}
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Additional Options
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.rushOrder}
                      onChange={(e) => handleInputChange('rushOrder', e.target.checked)}
                    />
                  }
                  label={`Rush Order (+${formatMoney(15)})`}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.giftWrapping}
                      onChange={(e) => handleInputChange('giftWrapping', e.target.checked)}
                    />
                  }
                  label={`Gift Wrapping (+${formatMoney(5)})`}
                />
              </Box>
              
              <TextField
                fullWidth
                label="Special Instructions"
                multiline
                rows={3}
                value={formData.specialInstructions}
                onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                placeholder="Any special requests or instructions for your order..."
              />
            </CardContent>
          </Card>
        );
        
      case 3:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" component="h3" gutterBottom>
                <Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />
                Review & Submit
              </Typography>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Please review all information before submitting your order.
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Customer Information
                  </Typography>
                  <Typography variant="body2">{formData.firstName} {formData.lastName}</Typography>
                  <Typography variant="body2">{formData.email}</Typography>
                  <Typography variant="body2">{formData.phone}</Typography>
                  {formData.company && <Typography variant="body2">{formData.company}</Typography>}
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Shipping Address
                  </Typography>
                  <Typography variant="body2">{formData.address}</Typography>
                  <Typography variant="body2">{formData.city}, {formData.state} {formData.zipCode}</Typography>
                  <Typography variant="body2">{formData.country}</Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Order Summary
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Subtotal:</Typography>
                        <Typography variant="body2">{formatMoney(calculateSubtotal())}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Shipping:</Typography>
                        <Typography variant="body2">{formatMoney(calculateShipping())}</Typography>
                      </Box>
                      {formData.rushOrder && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Rush Order:</Typography>
                          <Typography variant="body2">{formatMoney(15)}</Typography>
                        </Box>
                      )}
                      {formData.giftWrapping && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Gift Wrapping:</Typography>
                          <Typography variant="body2">{formatMoney(5)}</Typography>
                        </Box>
                      )}
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6" fontWeight="bold">Total:</Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          {formatMoney(
                            calculateTotal() +
                              (formData.rushOrder ? 15 : 0) +
                              (formData.giftWrapping ? 5 : 0)
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.agreeToTerms}
                        onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                      />
                    }
                    label="I agree to the terms and conditions *"
                  />
                  {errors.agreeToTerms && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      {errors.agreeToTerms}
                    </Typography>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.marketingEmails}
                        onChange={(e) => handleInputChange('marketingEmails', e.target.checked)}
                      />
                    }
                    label="Send me updates about new designs and promotions (optional)"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
        
      default:
        return null;
    }
  };

  const renderBreadcrumbs = () => (
    <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
      <MuiLink component={Link} to="/" color="inherit">
        Home
      </MuiLink>
      <MuiLink component={Link} to={`/${orderData?.category}`} color="inherit">
        {orderData?.category ? orderData.category.charAt(0).toUpperCase() + orderData.category.slice(1) : 'Category'}
      </MuiLink>
      <Typography color="text.primary">
        Order Form
      </Typography>
    </Breadcrumbs>
  );

  if (!orderData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          No Order Data
        </Typography>
        <Button component={Link} to="/" variant="contained">
          Back to Home
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {renderBreadcrumbs()}
      
      <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 4 }}>
        Complete Your Order
      </Typography>
      
      <Grid container spacing={4}>
        <Grid item xs={12} lg={8}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {renderStepContent(activeStep)}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
            >
              Back
            </Button>
            
            <Box>
              {activeStep === STEPS.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  startIcon={<CheckCircle />}
                  size="large"
                >
                  Submit Order
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  size="large"
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Grid>
        
        <Grid item xs={12} lg={4}>
          {renderOrderSummary()}
        </Grid>
      </Grid>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default OrderForm;
